import { Avatar } from "../Avatar";
import { ColorKeys } from "@/theme/colors";
import "./CardDoctorInfo.scss";
import clsx from "clsx";
import { Divider } from "@/components/ui/Divider";
import { CategoryHeader, Col, Row } from "@/components";
import { useState } from "react";
import React from "react";
import { DayHours } from "@anoki/fse-common";
import { DoctorSchedule } from "../DoctorSchedule";
import { HeadingLevel } from "@/types/headings";

type Info = {
  label: string;
  value: string;
};

type ScheduleDay = {
  label: string;
  slots: DayHours[];
};

type OfficeInfo = {
  info: Info[];
  schedule?: {
    label: string;
    days: ScheduleDay[];
  };
};

type CardDoctorInfoProps = {
  titleTag?: HeadingLevel;
  doctorName: string;
  specialties?: string[];
  contactInfo?: Info[];
  avatar?: {
    initials: string;
    bg?: ColorKeys;
    textColor?: ColorKeys;
  };
  className?: string;
  officesInfo?: Info[][];
  officesInfoV2?: OfficeInfo[];
  officeLabel?: string;
};

export const CardDoctorInfo = ({
  titleTag: TitleTag = "h1",
  doctorName,
  specialties,
  contactInfo,
  avatar,
  className,
  officesInfo = [],
  officesInfoV2 = [],
  officeLabel = "Ambulatorio",
}: CardDoctorInfoProps) => {
  const [openOffices, setOpenOffices] = useState<number[]>([]);

  const showContactInfo = () => {
    return contactInfo && contactInfo?.length > 0;
  };

  const toggleOffice = (index: number) => {
    if (openOffices.includes(index)) {
      setOpenOffices(openOffices.filter((i) => i !== index));
    } else {
      setOpenOffices([...openOffices, index]);
    }
  };

  return (
    <article
      className={clsx("bg-white shadow br-x4 ui-card-doctor-info", className)}
    >
      <Row as={"header"} gap={"x16"}>
        {avatar && (
          <Avatar
            textColor={avatar?.textColor ?? "slate-20"}
            bg={avatar?.bg ?? "lightblue-a01"}
            initials={avatar?.initials ?? ""}
            className="ui-card-doctor-avatar"
            size="3"
          />
        )}

        <div>
          <TitleTag className="fs-5 fw-semibold text-slate-20">
            {doctorName}
          </TitleTag>
          {specialties && specialties?.length > 0 && (
            <p className="fs-6 text-slate-20 fw-regular">
              {specialties?.join(" | ")}
            </p>
          )}
        </div>
      </Row>
      <div className="ui-card-doctor-content-info">
        {showContactInfo() && (
          <dl className="ui-card-doctor-contact-info">
            {contactInfo?.map((info) => (
              <div
                key={info.label}
                role="group"
                className="ui-card-doctor-contact-detail-info"
              >
                <dt className="fs-6 fw-bold text-slate-28">{info.label}</dt>
                <dd className="fs-6 fw-regular  text-slate-28">{info.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {officesInfo?.length > 0 && (
          <Divider
            bg="border-divider"
            className="ui-card-doctor-divider-info mt-x24"
          />
        )}

        {officesInfoV2?.length > 0 && (
          <Divider
            bg="border-divider"
            className="ui-card-doctor-divider-info mt-x24"
          />
        )}

        <Col>
          {officesInfo?.length > 0 &&
            officesInfo?.map((office, index) => (
              <React.Fragment key={index}>
                <CategoryHeader
                  title={`${officeLabel} ${index + 1}`}
                  isOpen={openOffices.includes(index)}
                  onToggle={() => toggleOffice(index)}
                  hasBorder={!openOffices.includes(index)}
                  className={"pt-x24"}
                  titleClassName={"fw-semibold fs-6 text-primary pb-x16"}
                />
                {openOffices.includes(index) && (
                  <div className={clsx("ui-has-border-bottom", "pb-x24")}>
                    {office.length > 0 && (
                      <dl className="ui-card-doctor-contact-info">
                        {office?.map((info) => (
                          <div
                            key={info.label}
                            role="group"
                            className="ui-card-doctor-contact-detail-info"
                          >
                            <dt className="fs-6 fw-bold text-slate-28">
                              {info.label}
                            </dt>
                            <dd className="fs-6 fw-regular  text-slate-28">
                              {info.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}

          {officesInfoV2?.length > 0 &&
            officesInfoV2?.map((office, index) => (
              <React.Fragment key={index}>
                <CategoryHeader
                  title={`${officeLabel} ${index + 1}`}
                  isOpen={openOffices.includes(index)}
                  onToggle={() => toggleOffice(index)}
                  hasBorder={!openOffices.includes(index)}
                  className={"pt-x24"}
                  titleClassName={"fw-semibold fs-6 text-primary pb-x16"}
                />
                {openOffices.includes(index) && (
                  <div className={clsx("ui-has-border-bottom", "pb-x24")}>
                    {office.info.length > 0 && (
                      <dl className="ui-card-doctor-contact-info">
                        {office?.info.map((info) => (
                          <div
                            key={info.label}
                            role="group"
                            className="ui-card-doctor-contact-detail-info"
                          >
                            <dt className="fs-6 fw-bold text-slate-28">
                              {info.label}
                            </dt>
                            <dd className="fs-6 fw-regular  text-slate-28">
                              {info.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {office.schedule && (
                      <dl className={"mt-x16"}>
                        <DoctorSchedule
                          {...office.schedule}
                          labelClassName={"fs-6 fw-bold text-slate-28"}
                        />
                      </dl>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
        </Col>
      </div>
    </article>
  );
};
